import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { VoucherErrorReason, VoucherPreview } from '@gg/shared';
import { Button, TextField } from '@/components/ui';
import { formatPrice } from '@/lib/i18n';

/**
 * "Code promo" field of screen 02 (P-03), always shown (product owner, 2026-10-29): one code
 * per order, checked by the API; shows the discount or the reason the code was refused.
 */
export function VoucherField({
  code: requested,
  applied,
  preview,
  checking,
  error,
  onApply,
  onRemove,
}: {
  /** Code entered and sent for checking (restored from the draft after a reload). */
  code?: string;
  /** The same code once the API accepted it. */
  applied?: string;
  preview?: VoucherPreview;
  checking: boolean;
  error: VoucherErrorReason | null;
  onApply: (code: string) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const inputId = useId();
  const [code, setCode] = useState(requested ?? '');

  return (
    <div className="border-t border-line pt-3">
      <label htmlFor={inputId} className="block text-sm font-semibold">
        {t('voucher.label')}
      </label>
      {preview && applied ? (
        <div className="mt-2.5 flex items-center justify-between gap-3 text-sm">
          <span role="status" className="font-semibold text-success">
            {t('voucher.applied', {
              code: preview.code,
              amount: formatPrice(preview.discountCents),
            })}
          </span>
          <div className="shrink-0">
            <Button variant="ghost" size="md" onClick={onRemove}>
              {t('voucher.remove')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-2.5 flex items-start gap-2">
          <div className="flex-1">
            <TextField
              id={inputId}
              placeholder={t('voucher.placeholder')}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              className="uppercase"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => {
                // Enter applies the code instead of submitting the checkout form.
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (code.trim()) onApply(code.trim().toUpperCase());
                }
              }}
              error={error && t(`voucher.error.${error}`)}
            />
          </div>
          <div className="shrink-0">
            <Button
              variant="secondary"
              size="inline"
              loading={checking}
              disabled={!code.trim()}
              onClick={() => onApply(code.trim().toUpperCase())}
            >
              {t('voucher.apply')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
